import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, Music, CalendarDays,
  Wallet, Receipt, BarChart3, LogOut, Menu, ClipboardCheck, Settings, BookOpen,
  AlertTriangle, MessageCircle, Moon, Sun,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { waLink } from "@/lib/whatsapp";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["administrator", "keuangan"] },
  { to: "/siswa", label: "Data Siswa", icon: Users, roles: ["administrator", "customer_service"] },
  { to: "/guru", label: "Data Guru", icon: GraduationCap, roles: ["administrator", "customer_service"] },
  { to: "/kelas", label: "Kelas & Instrumen", icon: Music, roles: ["administrator", "customer_service"] },
  { to: "/jadwal", label: "Jadwal Les", icon: CalendarDays, roles: ["administrator", "customer_service"] },
  { to: "/absensi", label: "Absensi", icon: ClipboardCheck, roles: ["administrator", "customer_service"] },
  { to: "/spp", label: "Pembayaran", icon: Wallet, roles: ["administrator", "keuangan"] },
  { to: "/pengeluaran", label: "Pengeluaran", icon: Receipt, roles: ["administrator", "keuangan"] },
  { to: "/jurnal", label: "Jurnal Manual", icon: BookOpen, roles: ["administrator", "keuangan"] },
  { to: "/laporan", label: "Laporan Keuangan", icon: BarChart3, roles: ["administrator", "keuangan"] },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings, roles: ["administrator"] },
];

const ROLE_LABELS = { administrator: "Administrator", keuangan: "Keuangan", customer_service: "Customer Service" };

function NavItems({ onNavigate, role }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((item) => !item.roles || item.roles.includes(role)).map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
            }`
          }
        >
          <Icon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function tenantActive(t) {
  if (!t) return true;
  const today = new Date().toISOString().slice(0, 10);
  return (t.status === "aktif" || t.status === "trial") && (!t.due_date || t.due_date >= today);
}

function SubscriptionBlocked({ tenant }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const pending = tenant?.status === "menunggu";
  const wa = cfg?.owner_wa
    ? waLink(
        cfg.owner_wa,
        `Halo, saya admin ${tenant?.name || "sekolah"}. Saya ingin ${pending ? "mengaktifkan" : "memperpanjang"} langganan ${cfg.product_name || "Cantata"} untuk sekolah kami.`
      )
    : null;
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 dark:bg-[#040D1D]" data-testid="subscription-blocked">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-[#071731]">
        <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${pending ? "bg-amber-100" : "bg-red-100"}`}>
          <AlertTriangle className={`h-7 w-7 ${pending ? "text-amber-600" : "text-red-600"}`} />
        </div>
        <h1 className="font-heading text-2xl font-bold" data-testid="blocked-title">
          {pending ? "Menunggu Pembayaran" : "Langganan Berakhir"}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {pending
            ? `Akun ${tenant?.name || "sekolah Anda"} sudah terdaftar dan sedang menunggu aktivasi. Silakan selesaikan pembayaran langganan — tim kami mengaktifkan akun maksimal 1x24 jam setelah pembayaran terkonfirmasi.`
            : `Langganan ${tenant?.name || "sekolah Anda"} telah berakhir atau dinonaktifkan. Silakan perpanjang langganan untuk kembali menggunakan aplikasi.`}
        </p>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" className="mt-6 block" data-testid="blocked-wa-button">
            <Button className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
              <MessageCircle className="h-4 w-4" /> Hubungi Kami via WhatsApp
            </Button>
          </a>
        )}
        <Button
          variant="outline"
          className="mt-3 w-full"
          data-testid="blocked-logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
        >
          Keluar
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const exitImpersonate = async () => {
    try {
      await api.post("/admin/exit-impersonate");
      await refresh();
      navigate("/superadmin");
    } catch { /* abaikan */ }
  };

  useEffect(() => {
    const dark = user?.tenant?.theme === "dark";
    document.documentElement.classList.toggle("dark", dark);
    return () => document.documentElement.classList.remove("dark");
  }, [user?.tenant?.theme]);

  const toggleTheme = async () => {
    const next = user?.tenant?.theme === "dark" ? "light" : "dark";
    try {
      await api.put("/tenant/theme", { theme: next });
      await refresh();
    } catch { /* abaikan */ }
  };

  if (user?.tenant && !tenantActive(user.tenant)) {
    return <SubscriptionBlocked tenant={user.tenant} />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-64 flex-col border-r border-border bg-white dark:border-white/10 dark:bg-[#071731] lg:flex">
        <div className="px-5 py-5">
          {user?.tenant?.logo_url ? (
            <img src={user.tenant.logo_url} alt={user.tenant.name} className="w-full object-contain" data-testid="sidebar-logo" />
          ) : (
            <div className="flex items-center gap-3" data-testid="sidebar-logo">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary font-heading text-lg font-bold text-primary-foreground">
                {(user?.tenant?.name || "C").charAt(0)}
              </div>
              <p className="font-heading text-base font-bold leading-tight">{user?.tenant?.name || "Cantata"}</p>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <NavItems role={user?.role} />
        </div>
        <div className="border-t border-border p-4 dark:border-white/10">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            className="mb-3 flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
          >
            {user?.tenant?.theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {user?.tenant?.theme === "dark" ? "Mode Terang" : "Mode Gelap"}
          </button>
          <div className="mb-3 px-2">
            <p className="text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
            <p className="mt-1 text-xs font-semibold text-accent" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Keluar
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        {user?.impersonating && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="impersonation-banner">
            <span>Anda sedang masuk sebagai <strong>{user.impersonating}</strong> (mode Super Admin)</span>
            <Button size="sm" variant="outline" onClick={exitImpersonate} data-testid="exit-impersonate-button">
              Kembali ke Super Admin
            </Button>
          </div>
        )}
        <header className="flex items-center gap-3 border-b border-border bg-white px-4 py-3 dark:border-white/10 dark:bg-[#071731] lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 pt-6 dark:border-white/10 dark:bg-[#071731]">
              <div className="mb-4 px-6">
                {user?.tenant?.logo_url ? (
                  <img src={user.tenant.logo_url} alt={user.tenant.name} className="w-full object-contain" data-testid="mobile-logo" />
                ) : (
                  <div className="flex items-center gap-3" data-testid="mobile-logo">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary font-heading text-lg font-bold text-primary-foreground">
                      {(user?.tenant?.name || "C").charAt(0)}
                    </div>
                    <p className="font-heading text-base font-bold leading-tight">{user?.tenant?.name || "Cantata"}</p>
                  </div>
                )}
              </div>
              <NavItems onNavigate={() => setOpen(false)} role={user?.role} />
              <div className="absolute bottom-0 left-0 right-0 border-t border-border p-4 dark:border-white/10">
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-button-mobile"
                  className="mb-3 flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
                >
                  {user?.tenant?.theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {user?.tenant?.theme === "dark" ? "Mode Terang" : "Mode Gelap"}
                </button>
                <Button variant="outline" className="w-full justify-start gap-2 dark:border-white/15 dark:bg-transparent dark:text-slate-200 dark:hover:bg-white/10" onClick={handleLogout} data-testid="mobile-logout-button">
                  <LogOut className="h-4 w-4" /> Keluar
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.tenant?.logo_url ? (
            <img src={user.tenant.logo_url} alt={user.tenant.name} className="h-9 w-auto object-contain" data-testid="mobile-header-logo" />
          ) : (
            <div className="flex items-center gap-2" data-testid="mobile-header-logo">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary font-heading text-sm font-bold text-primary-foreground">
                {(user?.tenant?.name || "C").charAt(0)}
              </div>
              <p className="font-heading text-sm font-bold">{user?.tenant?.name || "Cantata"}</p>
            </div>
          )}
        </header>
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
